'use client';

import React, { useState } from 'react';
import { RootState } from '@/store';
import { useAppSelector } from '@/store/hooks';
import { refreshActiveTable } from '@/store/reducers/table-slice';
import { getUi, hideModal } from '@/store/reducers/ui-slice';
import { AdminRoles, Permission } from '@alien-platform/bo-sdk-v2';
import { MethodResponse } from '@alien-platform/cms-bo';
import { Button, Grid } from '@mui/material';
import { Stack } from '@mui/system';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'sonner';
import { z } from 'zod';

import { CustomConfigInputProps, InputType } from '@/types/general';
import { CustomConfigInputV2 } from '@/components/core/custom-config-input-v2';

function EditPermissionModalContent(): React.JSX.Element {
  const { modal } = useAppSelector(getUi);
  const { user } = useSelector((state: RootState) => state.user);
  const content = modal?.content as Permission;

  const [dto, setDto] = useState({
    id: content.id,
    opId: user?.opId,
    name: content.name,
    desc: content.desc,
    requiredMenuItems: content.requiredMenuItems,
    allowedRoles: content.allowedRoles,
    tags: content.tags,
  });

  const generalInputs: CustomConfigInputProps[] = [
    {
      label: 'Title',
      name: 'name',
      value: dto.name,
      type: InputType.INPUT,
      zod: z.string().min(1, 'Name is required'),
    },
    {
      label: 'Description',
      name: 'desc',
      value: dto.desc,
      type: InputType.INPUT,
      zod: z.string().min(1, 'Desc is required'),
    },

    {
      label: 'Required Menu Items',
      name: 'requiredMenuItems',
      value: dto.requiredMenuItems,
      type: InputType.MULTISELECT,
      menuItems: [
        { label: 'staff_settings', value: 'staff_settings' },
        { label: 'list_staff', value: 'list_staff' },
      ],
    },
    // {
    //   label: 'Required Permissions',
    //   name: 'requiredPermissions',
    //   value: dto.requiredPermissions,
    //   type: InputType.SELECT,
    // },
    {
      label: 'Allowed Roles',
      name: 'allowedRoles',
      value: dto.allowedRoles,
      type: InputType.MULTISELECT,
      menuItems: Object.values(AdminRoles).map((item) => ({ label: item.replace(/_/g, ' '), value: item })),
    },
    {
      label: 'Tags',
      name: 'tags',
      value: dto.tags,
      type: InputType.INPUT,
    },
  ];

  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const handleClose = (): void => {
    dispatch(hideModal());
  };

  const handleSubmit = async (): Promise<void> => {
    setIsLoading(true);

    try {
      const response = await fetch(`/api/permissions`, {
        method: 'PUT',
        body: JSON.stringify(dto),
      });
      const result: MethodResponse<Permission> = await response.json();
      if (response.ok && result.success) {
        toast.success('Permission updated successfully!');
        dispatch(hideModal());
        dispatch(refreshActiveTable());
      } else {
        toast.error(result.messages?.[0] || 'Error: 500 | Unable to update permission item');
      }
    } catch (e) {
      toast.error('Error: 500 | Unable to update permission item');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Stack spacing={3}>
      <Grid container spacing={4}>
        {generalInputs.map((item, index) => (
          <CustomConfigInputV2 key={index} defaultData={dto} setDefaultData={setDto} {...item} />
        ))}
      </Grid>
      <Stack direction="row" spacing={3} sx={{ mt: 3, justifyContent: 'initial' }}>
        <Button type="button" color="secondary" onClick={handleClose} sx={{ flex: '1 1 auto' }}>
          Close
        </Button>

        <Button
          loading={isLoading}
          onClick={handleSubmit}
          color="success"
          sx={{ flex: '1 1 auto' }}
          variant="contained"
        >
          Update Permission
        </Button>
      </Stack>
    </Stack>
  );
}

export default EditPermissionModalContent;
